import { useEffect, useCallback, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Snackbar, Alert } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import MiMenu from './components/miMenu.js';
import Login from './components/Login';

import useAuth from './hooks/useAuth';
import useIdleTimeout from './hooks/useIdleTimeout';

// Tiempo de inactividad: 15 minutos
const IDLE_TIMEOUT = 15 * 60 * 1000;

function App() {
  const { auth, setAuth } = useAuth();
  const [showLogoutMessage, setShowLogoutMessage] = useState(false);
  const [logoutReason, setLogoutReason] = useState('');

  // Funcion para cerrar sesion
  const handleLogout = useCallback((reason = 'inactividad') => {
    // Notificar a otras pestañas
    localStorage.setItem('logout', Date.now().toString());

    // Limpiar localStorage
    localStorage.removeItem('accessToken');
    localStorage.removeItem('inversionista');
    localStorage.removeItem('nivel');
    localStorage.removeItem('lastActivity');
    localStorage.removeItem('currentPage'); // Limpiar pagina guardada al cerrar sesion

    // Mostrar mensaje de cierre de sesion
    setLogoutReason(reason);
    setShowLogoutMessage(true);

    // Resetear estado de autenticacion
    setAuth({
      accessToken: "",
      usuario: 0,
      inversionista: 0,
      nivel: 2
    });
  }, [setAuth]);

  // Hook de inactividad - solo activo si hay sesion
  const { showWarning, remainingTime, dismissWarning } = useIdleTimeout(
    handleLogout,
    IDLE_TIMEOUT,
    !!auth.accessToken  // Solo activo cuando hay sesión
  );

  useEffect(() => {
    if (!auth.accessToken) {
      let cookie = localStorage.getItem("accessToken") || "";
      let inversionista = localStorage.getItem("inversionista") || 0;
      let nivel = localStorage.getItem("nivel") || 0;

      // Verificar si hay una razon de cierre de sesion guardada
      const savedLogoutReason = localStorage.getItem('logoutReason');
      if (savedLogoutReason) {
        setLogoutReason(savedLogoutReason);
        setShowLogoutMessage(true);
        localStorage.removeItem('logoutReason');
      }

      if (cookie.length > 0) {
        setAuth({ accessToken: cookie, inversionista: inversionista, nivel: nivel })
      }
    }
  }, [])

  useEffect(() => {
  }, [auth])

  // Dialogo de advertencia de cierre de sesion
  const WarningDialog = () => (
    <Dialog
      open={showWarning && !!auth.accessToken}
      onClose={dismissWarning}
      PaperProps={{
        sx: {
          borderRadius: 2,
          border: '2px solid #d4af37',
          minWidth: 350
        }
      }}
    >
      <DialogTitle sx={{
        background: 'linear-gradient(135deg, #1e3a5f 0%, #2c5282 100%)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        borderBottom: '2px solid #d4af37'
      }}>
        <WarningAmberIcon sx={{ color: '#d4af37' }} />
        Sesion por expirar
      </DialogTitle>
      <DialogContent sx={{ pt: 3, textAlign: 'center' }}>
        <Typography variant="body1" gutterBottom>
          Su sesion se cerrara automaticamente por inactividad en:
        </Typography>
        <Box sx={{
          my: 2,
          p: 2,
          backgroundColor: '#fff3cd',
          borderRadius: 2,
          border: '1px solid #ffc107'
        }}>
          <Typography variant="h3" sx={{ color: '#856404', fontWeight: 'bold' }}>
            {remainingTime}
          </Typography>
          <Typography variant="body2" sx={{ color: '#856404' }}>
            segundos
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Mueva el mouse o presione cualquier tecla para continuar
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
        <Button
          variant="contained"
          onClick={dismissWarning}
          sx={{
            background: 'linear-gradient(135deg, #1e3a5f 0%, #2c5282 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #2c5282 0%, #3d6a9f 100%)',
            }
          }}
        >
          Continuar trabajando
        </Button>
        <Button
          variant="outlined"
          onClick={() => handleLogout('manual')}
          sx={{
            color: '#dc2626',
            borderColor: '#dc2626',
            '&:hover': {
              backgroundColor: 'rgba(220, 38, 38, 0.1)',
              borderColor: '#dc2626'
            }
          }}
        >
          Cerrar sesion ahora
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Mensaje de cierre de sesion
  const getLogoutMessage = () => {
    switch (logoutReason) {
      case 'inactividad':
        return 'Su sesion ha sido cerrada por inactividad. Por favor, inicie sesion nuevamente.';
      case 'manual':
        return 'Ha cerrado sesion correctamente.';
      default:
        return 'Su sesion ha sido cerrada.';
    }
  };

  return (
    <>
      {!auth.accessToken ? <Login /> : null}
      {auth.accessToken ? <MiMenu /> : null}
      <WarningDialog />

      {/* Snackbar de notificacion de cierre de sesion */}
      <Snackbar
        open={showLogoutMessage}
        autoHideDuration={6000}
        onClose={() => setShowLogoutMessage(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowLogoutMessage(false)}
          severity={logoutReason === 'manual' ? 'success' : 'warning'}
          variant="filled"
          sx={{
            width: '100%',
            fontSize: '1rem',
            '& .MuiAlert-icon': {
              fontSize: '1.5rem'
            }
          }}
        >
          {getLogoutMessage()}
        </Alert>
      </Snackbar>
    </>
  );
}

export default App;
